import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle2, Send, Loader2 } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  { value: "general", label: "General inquiry" },
  { value: "press", label: "Press / Media" },
  { value: "advertise", label: "Advertise on Newseze" },
  { value: "partnership", label: "Partnership / Syndication" },
  { value: "bug", label: "Bug report" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", intent: "general" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 12000 });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[760px] mx-auto px-5 py-10">
        <div className="section-title">
          <span className="pip" />
          <h2 data-testid="contact-title">Contact Newseze</h2>
        </div>
        <p className="text-[14px] text-[var(--muted)] mt-1 mb-6 leading-snug">
          For press, partnership, advertising, bug reports, or general questions — we read every message. AI routes
          your note to the right desk; a human responds within 24 hours on business days.
        </p>

        {done ? (
          <div className="rounded-xl border border-[var(--line)] bg-[var(--sky)] p-6 text-center" data-testid="contact-success">
            <CheckCircle2 size={28} className="text-[var(--blue)] mx-auto mb-2" />
            <div className="text-[18px] font-black">Thanks, {form.name.split(" ")[0]}.</div>
            <p className="text-[13px] text-[var(--muted)] mt-1">We'll be in touch at {form.email}.</p>
            <Link to="/" className="btn primary mt-4" data-testid="contact-back">Back to Newseze</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 bg-white rounded-xl border border-[var(--line)] p-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Your name</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120}
                       className="field w-full mt-1" placeholder="Your full name" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Email</label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                       className="field w-full mt-1" placeholder="you@email.com" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Reason</label>
              <select value={form.intent} onChange={(e) => set("intent", e.target.value)}
                      className="field w-full mt-1" data-testid="contact-intent">
                {INTENTS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Subject</label>
              <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} maxLength={200}
                     className="field w-full mt-1" placeholder="What's this about?" data-testid="contact-subject" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Message</label>
              <textarea required value={form.message} onChange={(e) => set("message", e.target.value)}
                        minLength={10} maxLength={4000} rows={6}
                        className="field w-full mt-1" placeholder="Tell us what's on your mind…" data-testid="contact-message" />
            </div>
            {err && <div className="text-[13px] text-red-600" data-testid="contact-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn primary w-full justify-center" data-testid="contact-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Send message</>}
            </button>
            <p className="text-[10px] text-[var(--muted)] text-center mt-2 flex items-center justify-center gap-1">
              <Mail size={10} /> legal@newseze.com · responses within 24h on business days
            </p>
          </form>
        )}
      </main>
      <Footer />
    </>
  );
}
